import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { reduxForm, Field } from 'redux-form';
import {
    Button,
    Divider,
    Form,
    Modal,
    Header,
    Grid,
    Message
} from 'semantic-ui-react';

// Global Components
import HarvestEventPickTypeNameValue from 'components/HarvestEventPickTypeNameValue';
import HarvestEventEntityPathNamesReversedValue from 'components/HarvestEventEntityPathNamesReversedValue';
import HarvestEventHasUnsavedChangesGate from 'components/HarvestEventHasUnsavedChangesGate';
// @ts-ignore
import SubmittingButton from 'components/SubmittingButton';

// Global Actions
// @ts-ignore
import { requestEventPrintLabels } from 'services/containerGroups/actions';
import { updateHarvestEvent } from 'services/harvestEvents/actions';

// Global Selectors
import {
    makeSelectHarvestEventId,
    makeSelectHarvestEventStartDate
} from 'services/harvestEvents/selectors';

// Global Form
import {
    normalizers,
    formatters,
    validators,
    RenderField,
    RenderDropdown
} from 'forms';

// Global Utils
import { getHarvestEventColorGradeOptions } from 'services/harvestEvents/utils';

// Local Components
// @ts-ignore
import ContainerGroupErrorsMessage from './components/ContainerGroupErrorsMessage';
// @ts-ignore
import ContainerGroupWarningsMessage from './components/ContainerGroupWarningsMessage';
// @ts-ignore
import HarvestPrintPropertiesTypeahead from './components/HarvestPrintPropertiesTypeahead';

// Local Selectors
// @ts-ignore
import { makeSelectInitialValues } from './selectors';

// Styles
import './styles.scss';

// Constants
// @ts-ignore
import { FORM_NAME } from './constants';

// Constants
const EVENT_LABELS_CREATE = 'EVENT_LABELS_CREATE';
const EVENT_UPDATE = 'EVENT_UPDATE';

// Formatters
const { toNumber } = formatters;

// Validators
const { isRequired, isNumber, isInteger, maxValue1000 } = validators;

// Normalizers
const { toNull } = normalizers;

interface Props {
    harvestEventUuid: string;
    containerGroupId?: number;
    requestEventPrintLabels: Function;
    error?: boolean;
    handleSubmit: Function;
    startDate?: string;
    updateHarvestEvent: Function;
    onClose: Function;
    eventId?: number;
}

class EventPrintForm extends React.PureComponent<Props> {
    get disabled(): boolean {
        const { containerGroupId } = this.props;
        return !!containerGroupId;
    }

    handleEventClick = (formValues: Record<string, any>): void => {
        const { onClose, ...props } = this.props;
        const { harvestEvent } = formValues;
        props.updateHarvestEvent(harvestEvent);
        onClose();
    };

    handleClick = (values: Record<string, any>): void => {
        const { eventId } = this.props;
        const {
            numberOfLabels,
            ignoreWarningsForContainerGroupId,
            harvestEvent: {
                harvestEvent: {
                    colorGrade,
                    harvestBinSuitabilityId,
                    harvestPalletTypeId,
                    harvestStorageTypeId,
                    harvestSubmissionProfileId,
                    harvestGrowingMethodId,
                    harvestLocationId,
                    harvestExporterId,
                    harvestBrandId
                }
            }
        } = values;
        this.props.requestEventPrintLabels(eventId, {
            numberOfLabels,
            ignoreWarningsForContainerGroupId,
            printProperties: {
                colorGrade,
                harvestBinSuitabilityId,
                harvestPalletTypeId,
                harvestStorageTypeId,
                harvestSubmissionProfileId,
                harvestGrowingMethodId,
                harvestLocationId,
                harvestExporterId,
                harvestBrandId
            }
        });
    };

    render(): React.ReactElement {
        const { disabled } = this;
        const {
            containerGroupId,
            harvestEventUuid,
            error,
            startDate,
            handleSubmit,
            onClose
        } = this.props;

        const numberOfLabelsValidate = startDate
            ? undefined
            : [isRequired, isNumber, isInteger, maxValue1000];

        return (
            <React.Fragment>
                <Modal.Header>
                    <HarvestEventPickTypeNameValue
                        harvestEventUuid={harvestEventUuid}
                    />
                    {' / '}
                    <HarvestEventEntityPathNamesReversedValue
                        harvestEventUuid={harvestEventUuid}
                    />
                </Modal.Header>
                <Modal.Content>
                    <Header>
                        {startDate
                            ? 'Create Bin Labels'
                            : 'Edit Print Properties'}
                    </Header>
                    <ContainerGroupErrorsMessage
                        containerGroupId={containerGroupId}
                    />
                    <ContainerGroupWarningsMessage
                        containerGroupId={containerGroupId}
                    />
                    <Form autoComplete="off">
                        <HarvestPrintPropertiesTypeahead
                            harvestPrintPropertyType="harvestBinSuitabilities"
                            name="harvestEvent.harvestEvent.harvestBinSuitabilityId"
                            label="Bin suitability"
                            placeholder="Add bin suitability"
                            validate={isRequired}
                            disabled={disabled}
                        />
                        <HarvestPrintPropertiesTypeahead
                            harvestPrintPropertyType="harvestPalletTypes"
                            name="harvestEvent.harvestEvent.harvestPalletTypeId"
                            label="Pallet Type"
                            placeholder="Add pallet type"
                            validate={isRequired}
                            disabled={disabled}
                        />
                        <HarvestPrintPropertiesTypeahead
                            harvestPrintPropertyType="harvestStorageTypes"
                            name="harvestEvent.harvestEvent.harvestStorageTypeId"
                            label="Proposed Storage"
                            placeholder="Add storage"
                            validate={isRequired}
                            disabled={disabled}
                        />
                        <HarvestPrintPropertiesTypeahead
                            harvestPrintPropertyType="harvestSubmissionProfiles"
                            name="harvestEvent.harvestEvent.harvestSubmissionProfileId"
                            label="Submission profile"
                            placeholder="Add submission profile"
                            disabled={disabled}
                            normalize={toNull}
                        />
                        <HarvestPrintPropertiesTypeahead
                            harvestPrintPropertyType="harvestGrowingMethods"
                            name="harvestEvent.harvestEvent.harvestGrowingMethodId"
                            label="Growing method"
                            placeholder="Add growing method"
                            validate={isRequired}
                            disabled={disabled}
                        />
                        <HarvestPrintPropertiesTypeahead
                            harvestPrintPropertyType="harvestLocations"
                            name="harvestEvent.harvestEvent.harvestLocationId"
                            label="Destination"
                            placeholder="Add destination"
                            validate={isRequired}
                            disabled={disabled}
                        />
                        <Field
                            component={RenderDropdown}
                            label="Proposed Colour (optional)"
                            name="harvestEvent.harvestEvent.colorGrade"
                            options={getHarvestEventColorGradeOptions()}
                            placeholder="Add proposed colour"
                            clearable={!disabled}
                            fluid
                            search
                            selection
                            disabled={disabled}
                            normalize={toNull}
                        />
                        <SubmittingButton
                            namespace={EVENT_UPDATE}
                            primary
                            onClick={handleSubmit(this.handleEventClick)}
                            disabled={disabled}
                            fluid
                        >
                            Save
                        </SubmittingButton>
                        {!!startDate && (
                            <HarvestEventHasUnsavedChangesGate
                                harvestEventUuid={harvestEventUuid}
                                FallbackComponent={
                                    <Message warning visible>
                                        <Message.Header>
                                            This pick has changes that are need
                                            uploading
                                        </Message.Header>
                                        <p>
                                            This will need to happen before you
                                            can create bin labels
                                        </p>
                                    </Message>
                                }
                            >
                                <React.Fragment>
                                    <Divider />
                                    <Field
                                        name="numberOfLabels"
                                        component={RenderField as any}
                                        label="Number of labels (required)"
                                        placeholder="Enter number of labels"
                                        type="number"
                                        validate={numberOfLabelsValidate}
                                        normalize={toNumber}
                                        disabled={disabled}
                                    />
                                    {error && (
                                        <Message negative>{error}</Message>
                                    )}
                                    <Grid>
                                        <Grid.Column
                                            mobile={16}
                                            tablet={8}
                                            computer={8}
                                        >
                                            <Button
                                                onClick={(): void => {
                                                    onClose();
                                                }}
                                                fluid
                                                basic
                                                primary
                                            >
                                                Cancel
                                            </Button>
                                        </Grid.Column>
                                        <Grid.Column
                                            mobile={16}
                                            tablet={8}
                                            computer={8}
                                        >
                                            <SubmittingButton
                                                namespace={EVENT_LABELS_CREATE}
                                                primary
                                                onClick={handleSubmit(
                                                    this.handleClick
                                                )}
                                                fluid
                                            >
                                                {containerGroupId
                                                    ? 'Continue'
                                                    : 'Create'}
                                            </SubmittingButton>
                                        </Grid.Column>
                                    </Grid>
                                </React.Fragment>
                            </HarvestEventHasUnsavedChangesGate>
                        )}
                    </Form>
                </Modal.Content>
            </React.Fragment>
        );
    }
}

const mapStateToProps = createStructuredSelector<any, any, any>({
    initialValues: makeSelectInitialValues(),
    startDate: makeSelectHarvestEventStartDate(),
    eventId: makeSelectHarvestEventId()
});

export default connect(mapStateToProps, {
    updateHarvestEvent,
    requestEventPrintLabels
})(
    reduxForm<any, any>({
        form: FORM_NAME,
        enableReinitialize: true
    })(EventPrintForm)
);
